import {
  Box,
  Dialog,
  Divider,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import ModalActionBar from 'components/modal-action-bar';
import DateInput from 'components/date-picker/date-picker';
import TimePicker from 'components/time-picker/time-picker';
import TextField from 'components/text-field';
import Checkbox from 'components/checkbox';
import React, {
  FC, useCallback, useEffect, useMemo, useState
} from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { TFunction } from 'i18next';

import ItemwithIcon from '../item-with-icon';
import AttendeeSelection from '../attendee-selection';
import { PartyItem, scheduleMettingTokens } from './services';
import { closestCorners, CollisionDetection, DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor, PointerSensor, pointerWithin, useSensor, useSensors } from '@dnd-kit/core';
import { DroppableList } from '../DroppableList';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { PartyDroppableItem } from '../DroppableList/components/InvolvedPartyItem';
import { orderPartiesAlphabetically, partyMatchesQuery, uniquePartiesById } from './utils';

const useStyles = makeStyles((theme: any) => ({
  surface: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    minWidth: 0
  },
  titleCard: {
    flex: '0 0 auto',
    padding: theme.spacing(3),
    minWidth: 0
  },
  dividerStyle: {
    flex: '0 0 auto',
    height: 1,
    width: '100%',
    backgroundColor: theme.accent.main
  },
  content: {
    flex: '1 1 auto',
    minHeight: 0,
    minWidth: 0,
    overflowY: 'auto',
    padding: theme.spacing(3)
  },
  contentPhone: { padding: theme.spacing(2) },
  fieldsSection: {
    flex: '0 0 auto',
    minWidth: 0,
    padding: theme.spacing(3, 3, 0)
  },
  fieldsSectionPhone: { padding: theme.spacing(2, 2, 0) },
  reissueRow: { marginTop: theme.spacing(1) },
  itemLast: { marginBottom: 0 },
  fontTitle: { color: theme.palette.text.primary, fontWeight: 500 },
  fieldsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    '& > *': { flex: '0 1 auto', minWidth: 0 }
  },
  fieldsRowPhone: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
    gap: theme.spacing(1.5),
    '& > *': { flex: '0 0 auto', width: '100%' }
  },
  timeRow: {
    display: 'flex',
    gap: theme.spacing(3),
    minWidth: 0
  },
  timeRowPhone: {
    gap: theme.spacing(1.5),
    '& > *': { flex: 1, minWidth: 0 }
  },
  pickerWrapper: {
    minWidth: 0,
    '& .MuiFilledInput-root': {
      minHeight: 40,
      height: 40
    },
    '& .MuiFilledInput-input': {
      lineHeight: '1.5',
      textAlign: 'right'
    }
  },
  listsRow: {
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
    minWidth: 0,
    '& > *': { minWidth: 0 }
  },
  actionBar: { flex: '0 0 auto' }
}));

type RonInviteSccreen = {
	t: TFunction;
	methods: UseFormReturn<scheduleMettingTokens, any>;
	open: boolean;
	onCancel: () => void;
	onClose: () => void;
	handleUpdateTokens: (data: scheduleMettingTokens, parties: PartyItem[], reissue: boolean) => void;
	loading: boolean;
	currentAttendees: PartyItem[];
	orderParties: PartyItem[];
	canReissue?: boolean;
}

const allPartiesContainerId = "allParties";
const attendeesContainerId = "attendees";

const RonScheduleMeeting: FC<RonInviteSccreen> = ({
  t,
  methods,
  open,
  onCancel,
  onClose,
  handleUpdateTokens,
  loading,
  currentAttendees,
  orderParties,
  canReissue = false
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const isCoarsePointer = useMediaQuery('(hover: none) and (pointer: coarse)', { noSsr: true });
  // Touch mode: one merged checkbox list replaces the drag/drop columns (phones and coarse-pointer tablets).
  const isTouchMode = isPhone || isCoarsePointer;
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const collisionDetectionStrategy: CollisionDetection = (args) => {
    const pointerIntersections = pointerWithin(args);

    if (pointerIntersections.length > 0) {
      const overContainer = pointerIntersections.find(
        ({ id }) => [allPartiesContainerId, attendeesContainerId].includes(id as string)
      );

      if (overContainer) {
        return [overContainer];
      }
    }

    return closestCorners(args);
  };

  const [allParties, setAllParties] = useState<PartyItem[]>(() => orderPartiesAlphabetically(
    orderParties.filter(({ _id }) => !currentAttendees.some(({ _id: attendeeId }) => attendeeId === _id))
  ));

  const [activePartyId, setActivePartyId] = useState<string | null>("");
  const [attendees, setAttendees] = useState<PartyItem[]>(() => orderPartiesAlphabetically(currentAttendees));
  const [partyFilter, setPartyFilter] = useState('');
  const [reissue, setReissue] = useState(false);

  // Render-only view: moveParty/findItemContainer must keep working on the full, unfiltered state.
  const filteredParties = useMemo(
    () => allParties.filter((party) => partyMatchesQuery(party, partyFilter)),
    [allParties, partyFilter]
  );

  const mergedParties = useMemo(
    () => orderPartiesAlphabetically(uniquePartiesById([...attendees, ...allParties])),
    [attendees, allParties]
  );
  const selectedIds = useMemo(() => new Set(attendees.map(({ _id }) => _id)), [attendees]);

  const [previousParties, setPreviousParties] = useState({ orderParties, currentAttendees });
  if (previousParties.orderParties !== orderParties || previousParties.currentAttendees !== currentAttendees) {
    setPreviousParties({ orderParties, currentAttendees });
    setAllParties(
      orderPartiesAlphabetically(
        orderParties.filter(({ _id }) => !currentAttendees.some(({ _id: attendeeId }) => attendeeId === _id))
      )
    );

    setAttendees(orderPartiesAlphabetically(currentAttendees));
  }

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActivePartyId(active.id as string);
  };

  const findItemContainer = useCallback((id: string) => {
    if (allParties.find(item => item._id === id)) return allPartiesContainerId;
    if (attendees.find(item => item._id === id)) return attendeesContainerId;
    return null;
  }, [allParties, attendees]);

  const activeParty: PartyItem | null = activePartyId
    ? orderParties.concat(attendees).find(({ _id }) => _id === activePartyId)!
    : null;

  // Single move path shared by drag/drop, the desktop arrow buttons and the touch checkboxes.
  const moveParty = useCallback((partyId: string, toContainerId: string) => {
    if (loading) return;

    const fromContainerId = findItemContainer(partyId);
    if (!fromContainerId || fromContainerId === toContainerId) return;

    const activeItem = fromContainerId === attendeesContainerId
      ? attendees.find(item => item._id === partyId)
      : allParties.find(item => item._id === partyId);
    if (!activeItem) return;

    // Functional updates + membership guards keep a rapid double-activation from duplicating a party.
    if (toContainerId === attendeesContainerId) {
      setAttendees(prevBundle => (
        prevBundle.some(({ _id }) => _id === activeItem._id)
          ? prevBundle
          : orderPartiesAlphabetically([activeItem, ...prevBundle])
      ));
      setAllParties(prevAll => prevAll.filter(({ _id }) => _id !== activeItem._id));
    } else {
      setAttendees(prevBundle => prevBundle.filter(({ _id }) => _id !== activeItem._id));
      setAllParties(prevAll => (
        prevAll.some(({ _id }) => _id === activeItem._id)
          ? prevAll
          : orderPartiesAlphabetically([...prevAll, activeItem])
      ));
    }
  }, [attendees, allParties, loading, findItemContainer]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActivePartyId(null);

    if (loading || !over) return;

    const overContainer = [allPartiesContainerId, attendeesContainerId].includes(over.id as string)
      ? over.id as string
      : findItemContainer(over.id as string);

    if (!overContainer) return;

    moveParty(active.id as string, overContainer);
  }, [moveParty, loading, findItemContainer]);

  const { control, setValue, handleSubmit } = methods;

  const startTime = useWatch({
    name: 'init_time',
    control
  });

  const endTime = useWatch({
    name: 'end_time',
    control
  });

  const date_meeting = useWatch({
    name: 'date',
    control
  });

  const today = new Date();

  const handleSave = useCallback(() => {
    if (loading) return;
    handleSubmit((data) => handleUpdateTokens(data, attendees, reissue))();
  }, [attendees, loading, reissue, handleSubmit, handleUpdateTokens]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      fullScreen={isPhone}
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={loading}
    >
      <Paper square={isPhone} className={classes.surface}>
        <Box className={classes.titleCard}>
          <Typography variant="h6" className={classes.fontTitle}>{t('ron:details')}</Typography>
        </Box>
        <Divider orientation="horizontal" className={classes.dividerStyle} />
        <Box className={clsx(classes.fieldsSection, isPhone && classes.fieldsSectionPhone)} aria-busy={loading}>
          <ItemwithIcon icon="calendar-alt" text={t('ron:schedule-date-time')} />
          <Box className={clsx(classes.fieldsRow, isPhone && classes.fieldsRowPhone)}>
            <Box className={classes.pickerWrapper}>
              <DateInput
                label={t('ron:date')}
                name=""
                minDate={today}
                value={date_meeting}
                disabled={loading}
                onChange={(date) => setValue('date', date || '')}
              />
            </Box>
            <Box className={clsx(classes.timeRow, isPhone && classes.timeRowPhone)}>
              <Box className={classes.pickerWrapper}>
                <TimePicker
                  value={startTime}
                  label={t('ron:start')}
                  disabled={loading}
                  onChange={(date) => {
									  setValue('init_time', date?.toString() || '');
                  }}
                />
              </Box>
              <Box className={classes.pickerWrapper}>
                <TimePicker
                  value={endTime}
                  label={t('ron:end')}
                  disabled={loading}
                  onChange={(date) => {
									  setValue('end_time', date?.toString() || '');
                  }}
                />
              </Box>
            </Box>
          </Box>
          {canReissue && (
            <Box className={classes.reissueRow}>
              <Checkbox
                name="reissue"
                label={t('ron:reissue-invitation')}
                checked={reissue}
                disabled={loading}
                handleOnChange={(_event, checked) => setReissue(checked)}
              />
            </Box>
          )}
        </Box>
        <Box className={clsx(classes.content, isPhone && classes.contentPhone)} aria-busy={loading}>
          <Box className={classes.itemLast}>
            <ItemwithIcon icon="user" text={t('ron:attendees')} />
            {isTouchMode ? (
              <AttendeeSelection
                t={t}
                parties={mergedParties}
                selectedIds={selectedIds}
                filter={partyFilter}
                onFilterChange={setPartyFilter}
                onToggle={(party, checked) => moveParty(party._id, checked ? attendeesContainerId : allPartiesContainerId)}
                loading={loading}
              />
            ) : (
              <Box className={classes.listsRow}>
                <DndContext
                  sensors={sensors}
                  collisionDetection={collisionDetectionStrategy}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <DroppableList
                    involvedParties={filteredParties}
                    title={t('orders:partiesLabel')}
                    droppableContainerId={allPartiesContainerId}
                    toolbar={(
                      <TextField
                        fullWidth
                        size="small"
                        disabled={loading}
                        value={partyFilter}
                        onChange={(e) => setPartyFilter(e.target.value)}
                        label={t('common:filter')}
                        inputProps={{ 'aria-label': t('common:filter'), autoComplete: 'off', type: 'search' }}
                      />
										)}
                    moveAction={{
										  kind: 'add',
										  disabled: loading,
										  onAction: (partyId) => moveParty(partyId, attendeesContainerId),
										  t
                    }}
                  />
                  <DroppableList
                    involvedParties={attendees}
                    title={t('orders:attendeesLabel')}
                    droppableContainerId={attendeesContainerId}
                    moveAction={{
										  kind: 'remove',
										  disabled: loading,
										  onAction: (partyId) => moveParty(partyId, allPartiesContainerId),
										  t
                    }}
                  />
                  <DragOverlay adjustScale={false} style={{ maxWidth: "500px", maxHeight: "100px" }}>
                    {activeParty ? (
                      <PartyDroppableItem party={activeParty} />
                    ) : null}
                  </DragOverlay>
                </DndContext>
              </Box>
            )}
          </Box>
        </Box>
        <Box className={classes.actionBar}>
          <ModalActionBar
            submitType="button"
            onSubmit={handleSave}
            isSubmitting={loading}
            submitDisabled={
							date_meeting === ''
							|| startTime === null
							|| endTime === null
							|| attendees.length === 0
						}
            submitText={t('ron:schedule-meeting')}
            onCancel={onCancel}
          />
        </Box>
      </Paper>
    </Dialog>
  );
};

export default RonScheduleMeeting;
